<template>
  <div>
    <!-- Overlay -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black bg-opacity-30 z-40"
        @click="close"
      ></div>
    </transition>

    <!-- Slide-out Panel -->
    <transition name="slide">
      <div
        v-if="isOpen"
        class="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col"
      >
        <!-- Header -->
        <div
          class="bg-blue-600 text-white px-4 py-3 flex items-center justify-between"
        >
          <h2 class="text-lg font-semibold flex items-center">
            <span class="mr-2">📜</span>
            Notification History
          </h2>
          <button
            @click="close"
            class="text-white hover:text-gray-200 text-2xl leading-none p-1"
          >
            &times;
          </button>
        </div>

        <!-- Filters -->
        <div
          class="px-4 py-3 border-b bg-gray-50 flex gap-2 flex-wrap text-black"
        >
          <select
            v-model="filters.status"
            @change="fetchNotifications"
            class="px-3 py-1.5 border rounded text-sm bg-white"
          >
            <option value="">All Status</option>
            <option value="sent">Pending</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <select
            v-model="filters.type"
            @change="fetchNotifications"
            class="px-3 py-1.5 border rounded text-sm bg-white"
          >
            <option value="">All Types</option>
            <option value="free_text">Free Text</option>
            <option value="new_order">New Order</option>
          </select>
        </div>

        <!-- Stats Bar -->
        <div
          class="px-4 py-2 border-b text-sm text-gray-600 bg-gray-50 flex justify-between items-center"
        >
          <span>
            Showing {{ notifications.length }} of {{ totalCount }} notifications
          </span>
          <span v-if="pendingCount > 0" class="text-orange-600 font-medium">
            {{ pendingCount }} pending
          </span>
        </div>

        <!-- Notifications List -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="loading" class="flex justify-center items-center h-32">
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
            ></div>
          </div>

          <div
            v-else-if="notifications.length === 0"
            class="flex flex-col items-center justify-center h-32 text-gray-500"
          >
            <span class="text-4xl mb-2">📭</span>
            <span>No notifications found</span>
          </div>

          <div v-else>
            <div
              v-for="notification in notifications"
              :key="notification.uuid"
              class="border-b hover:bg-gray-50 transition-colors"
              :class="getNotificationClass(notification)"
            >
              <div class="px-4 py-3">
                <!-- Header row -->
                <div class="flex items-start justify-between mb-1">
                  <div class="flex items-center gap-2">
                    <span class="text-lg">{{
                      getTypeIcon(notification.type)
                    }}</span>
                    <span class="font-medium text-gray-900">{{
                      notification.title
                    }}</span>
                  </div>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="getStatusBadgeClass(notification.status)"
                  >
                    {{ notification.status }}
                  </span>
                </div>

                <!-- Body -->
                <p class="text-gray-600 text-sm mb-2 line-clamp-2">
                  {{ notification.body }}
                </p>

                <!-- Meta info -->
                <div
                  class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500"
                >
                  <span
                    v-if="notification.created_by_name"
                    class="flex items-center gap-1"
                  >
                    <span>👤</span>
                    Sent by: {{ notification.created_by_name }}
                  </span>
                  <span class="flex items-center gap-1">
                    <span>📅</span>
                    Created: {{ formatDate(notification.createdAt) }}
                  </span>
                  <span
                    v-if="notification.sent_at"
                    class="flex items-center gap-1"
                  >
                    <span>📤</span>
                    Sent: {{ formatDate(notification.sent_at) }}
                  </span>
                  <span
                    v-if="notification.seenByName"
                    class="flex items-center gap-1"
                  >
                    <span>👁️</span>
                    {{
                      notification.status === "dismissed" ? "Dismissed" : "Seen"
                    }}
                    by: {{ notification.seenByName }}
                  </span>
                  <span
                    v-if="notification.seenAt"
                    class="flex items-center gap-1"
                  >
                    <span>🕐</span>
                    {{ formatDate(notification.seenAt) }}
                  </span>
                </div>

                <!-- Actions -->
                <div
                  v-if="notification.status === 'sent'"
                  class="mt-2 flex gap-2"
                >
                  <button
                    @click="dismissNotification(notification)"
                    class="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                  >
                    Dismiss
                  </button>
                  <button
                    v-if="notification.url && notification.url !== '/'"
                    @click="navigateToUrl(notification)"
                    class="text-xs px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                  >
                    View
                  </button>
                </div>
              </div>
            </div>

            <!-- Load More -->
            <div v-if="hasMore" class="p-4 text-center">
              <button
                @click="loadMore"
                :disabled="loadingMore"
                class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors disabled:opacity-50"
              >
                {{ loadingMore ? "Loading..." : "Load More" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "NotificationHistoryPanel",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      notifications: [],
      loading: false,
      loadingMore: false,
      totalCount: 0,
      pendingCount: 0,
      offset: 0,
      limit: 20,
      notificationsService: null,
      filters: {
        status: "",
        type: ""
      }
    };
  },
  computed: {
    hasMore() {
      return this.notifications.length < this.totalCount;
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.loadNotificationsService();
      }
    }
  },
  methods: {
    async loadNotificationsService() {
      if (this.notificationsService) {
        this.fetchNotifications();
        return;
      }

      try {
        this.notificationsService = await import(
          "@/api/NotificationsService.js"
        );
        this.fetchNotifications();
      } catch (e) {
        console.error("Failed to load notifications service:", e);
      }
    },
    async fetchNotifications() {
      this.loading = true;
      this.offset = 0;

      try {
        const response = await this.notificationsService.getNotificationHistory(
          {
            limit: this.limit,
            offset: 0,
            status: this.filters.status || undefined,
            type: this.filters.type || undefined
          }
        );

        if (response.status === "success") {
          this.notifications = response.notifications || [];
          this.totalCount = response.total_count || 0;
          this.pendingCount = response.pending_count || 0;
        }
      } catch (e) {
        console.error("Failed to fetch notifications:", e);
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      this.loadingMore = true;
      this.offset += this.limit;

      try {
        const response = await this.notificationsService.getNotificationHistory(
          {
            limit: this.limit,
            offset: this.offset,
            status: this.filters.status || undefined,
            type: this.filters.type || undefined
          }
        );

        if (response.status === "success" && response.notifications) {
          this.notifications = [
            ...this.notifications,
            ...response.notifications
          ];
        }
      } catch (e) {
        console.error("Failed to load more notifications:", e);
      } finally {
        this.loadingMore = false;
      }
    },
    async dismissNotification(notification) {
      try {
        await this.notificationsService.dismissNotification(notification.uuid);
        // Update local state with current user info
        const currentUser = this.$store.getters["auth/currentUser"];
        notification.status = "dismissed";
        notification.seen_by_name =
          currentUser?.name || currentUser?.user || "You";
        notification.seen_at = new Date().toISOString();
        this.pendingCount = Math.max(0, this.pendingCount - 1);
        this.$emit("notification-dismissed");
      } catch (e) {
        console.error("Failed to dismiss notification:", e);
      }
    },
    navigateToUrl(notification) {
      if (notification.url) {
        this.$router.push(notification.url);
        this.close();
      }
    },
    close() {
      this.$emit("close");
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      // Handle MySQL datetime format (YYYY-MM-DD HH:MM:SS) by replacing space with T
      const normalizedDate = dateStr.replace(" ", "T");
      const date = new Date(normalizedDate);

      // Check if date is valid
      if (isNaN(date.getTime())) return dateStr;

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    getTypeIcon(type) {
      const icons = {
        free_text: "📩",
        new_order: "📦",
        order_status: "🔄"
      };
      return icons[type] || "🔔";
    },
    getStatusBadgeClass(status) {
      const classes = {
        pending: "bg-yellow-100 text-yellow-800",
        sent: "bg-orange-100 text-orange-800",
        seen: "bg-blue-100 text-blue-800",
        dismissed: "bg-gray-100 text-gray-600"
      };
      return classes[status] || "bg-gray-100 text-gray-600";
    },
    getNotificationClass(notification) {
      if (notification.status === "sent") {
        return "bg-orange-50 border-l-4 border-l-orange-400";
      }
      return "";
    }
  }
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
